import { Input, useSidebar } from '@features/ui';
import { SearchIcon } from 'lucide-react';
import { useAuthStore } from '@stores/authStore.ts';
import { AvatarHeader } from '@features/core/ui/layout/avatarHeader';
import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchUsers } from '@src/services/users.ts';
import { UserSearchItem } from '@src/types/users.ts';

interface ContentHeaderProps {
  isUser?: boolean;
}
export const ContentHeader: FC<ContentHeaderProps> = ({ isUser }) => {
  const { auth } = useAuthStore();
  const { toggleSidebar } = useSidebar();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<UserSearchItem[]>([]);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Debounced search
  useEffect(() => {
    if (isUser) return; // search disabled for user layout
    const trimmed = query.trim();

    if (trimmed.length < 2) {
      setResults([]);
      setOpen(false);
      setHighlightIndex(-1);

      return;
    }

    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await searchUsers(trimmed);

        setResults(res.data || []);
        setOpen(true);
        setHighlightIndex(-1);
      } catch (e) {
        // swallow error for header search
        setResults([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(t);
  }, [query, isUser]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onClick);

    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const onSelect = (item: UserSearchItem) => {
    if (!item?.id) return;
    setOpen(false);
    setQuery('');
    navigate(`/admin/users/${item.id}`);
  };

  const displayFor = (item: UserSearchItem) => {
    const name = [item.first_name, item.last_name].filter(Boolean).join(' ');

    return name || item.username || item.email || `#${item.id}`;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightIndex >= 0 ? highlightIndex : 0;
      const picked = results[idx];

      if (picked) onSelect(picked);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  if (!auth.user) return <></>;

  return (
    <>
      {!isUser && (
        <div
          ref={containerRef}
          className="relative md:block hidden w-full max-w-[377px] min-w-[200px]"
        >
          <Input
            className={'w-full border-[#FFFFFF29] rounded-[27px]'}
            placeholder={'Search'}
            startIcon={<SearchIcon color={'#8B8B8B'} size={15} />}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {open && (
            <div className="absolute z-50 top-full left-0 mt-1 w-full rounded-md border border-[#2E2E2E] bg-background shadow-lg">
              <div className="max-h-72 overflow-auto py-1">
                {loading && (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    Searching...
                  </div>
                )}
                {!loading && results.length === 0 && (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    No results
                  </div>
                )}
                {!loading &&
                  results.map((item, idx) => (
                    <button
                      key={`${item.id}-${idx}`}
                      className={
                        'w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground ' +
                        (idx === highlightIndex
                          ? 'bg-accent text-accent-foreground'
                          : '')
                      }
                      onClick={() => onSelect(item)}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setHighlightIndex(idx)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate">{displayFor(item)}</span>
                        {item.username && (
                          <span className="ml-2 truncate text-xs text-muted-foreground">
                            {item.username}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="flex-1 justify-end flex md:hidden cursor-pointer">
        <AvatarHeader onClick={() => toggleSidebar(true)} />
      </div>
      <div className="flex-1 justify-end md:flex hidden">
        <div className="flex gap-x-5 items-center">
          {/* <NotificationIcon height={24} width={24} /> */}
          <div className={'flex items-center gap-x-2.5'}>
            {isUser ? (
              <AvatarHeader />
            ) : (
              <img
                alt="avatar"
                className="w-[44px] h-[44px] rounded-full object-cover border-2 border-blue-400"
                src="/assets/fake_avatar.png"
              />
            )}
            <div className={'gap-1'}>
              <p className="text-sm font-bold">{auth.user?.username}</p>
              {!isUser && <p className={'text-xs text-[#B3B3B3]'}>Admin</p>}
            </div>
            {/* <Button
              className={
                'h-[18px] w-[18px] border-[0.2px] rounded-[32px] border-[#5C5C5C]'
              }
              size="icon"
              variant="ghost"
            >
              <ChevronDown color={'#565656'} />
            </Button> */}
          </div>
        </div>
      </div>
    </>
  );
};
